import { initializeRootStore } from "@mirage-web/utils/root-store";
import { QueryClient } from "@tanstack/react-query";
import { HeadContent, Outlet, Scripts, createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Fragment } from "react";
import { createPortal } from "react-dom";
import { Toaster } from "sonner";

import { DefaultErrorComponent, NotFoundComponent } from "~/components/error-states";
import { SidebarProvider } from "~/components/ui/sidebar";
import { useAppearance } from "~/modules/appearance/store";
import { registerPrimaryStores } from "~/stores/registry";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RouteComponent,
  async beforeLoad() {
    const rootStart = performance.now();
    registerPrimaryStores();

    await initializeRootStore();

    return { rootStart };
  },

  notFoundComponent: NotFoundComponent,
  errorComponent: DefaultErrorComponent,
});

function RouteComponent() {
  const { resolved } = useAppearance();
  return (
    <Fragment>
      {createPortal(<HeadContent />, document.head)}
      <Scripts />
      <Toaster
        theme={resolved}
        richColors
        position="top-center"
        toastOptions={{
          className:
            "focus-visible:!outline-2 focus-visible:!outline-ring focus-visible:!outline-offset-2",
        }}
        style={
          {
            "--normal-bg": "var(--popover)",
            "--normal-text": "var(--popover-foreground)",
            "--normal-border": "var(--border)",
            "--normal-bg-hover": "var(--accent)",
            "--normal-border-hover": "var(--input)",
            "--success-bg": "var(--success-muted)",
            "--success-text": "var(--success-foreground)",
            "--success-border": "var(--oasis-toast-success-border)",
            "--info-bg": "var(--info-muted)",
            "--info-text": "var(--info-foreground)",
            "--info-border": "var(--oasis-toast-info-border)",
            "--warning-bg": "var(--warning-muted)",
            "--warning-text": "var(--warning-foreground)",
            "--warning-border": "var(--oasis-toast-warning-border)",
            "--error-bg": "var(--danger-muted)",
            "--error-text": "var(--danger-foreground)",
            "--error-border": "var(--danger-border)",
          } as React.CSSProperties
        }
      />

      <SidebarProvider>
        <Outlet />
      </SidebarProvider>

      <TanStackRouterDevtools position="bottom-right" />
    </Fragment>
  );
}
